import { Resizable } from "re-resizable";
import { makeStyles } from "tss-react/mui";
import { COLUMN_HEIGHT, SNAP_GAP } from "@miplatform/constants";
import { createUUID } from "@miplatform/utils";
import { useDraggable } from "@dnd-kit/core";
import { Box } from "@mui/material";
import { SchedulerEvent, SchedulerUpdates } from "../types";
import { useEventViewController } from "./useEventViewController";

export function Event({
  children,
  className,
  event,
  isInBucket = false,
  onDeselect = () => null,
  onEventUpdate,
  hasModalOpen,
  onSelect = () => null,
  zoom = 1,
  isOverLay,
}: Readonly<{
  children?: React.ReactNode;
  className?: string;
  event?: SchedulerEvent;
  isInBucket?: boolean;
  onDeselect?: () => void;
  onEventUpdate?: (item: SchedulerEvent, updates: SchedulerUpdates) => void;
  hasModalOpen?: boolean;
  onSelect?: () => void;
  zoom?: number;
  isOverLay?: boolean;
}>) {
  const fromBucket = isInBucket || !event?.assignedUserId;

  const disabled =
    event?.noDrag || event?.isProposed || event?.hasProposed || hasModalOpen;

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: event?.uuid ?? event?.id ?? createUUID(),
    data: { ...event, fromBucket },
    disabled,
  });

  const snapGap = SNAP_GAP * zoom;

  const top = event?.y || 0;

  const { extendedTime } = event ?? {};

  const { height, onResizeStop, calculateHeight } = useEventViewController({
    top,
    event,
    onEventUpdate,
    zoom,
  });

  const { classes } = useStyles({
    top: top * zoom,
  });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={
        className ? [classes.event, className].join(" ") : classes.event
      }
      onMouseDownCapture={onSelect}
      onMouseUpCapture={onDeselect}
      data-testid="event"
    >
      <Resizable
        as={Box}
        grid={[0, snapGap]}
        size={{
          height: calculateHeight({
            extendedTime,
            fromBucket,
            height,
            top,
            isOverLay,
          }),
        }}
        minHeight={snapGap}
        maxHeight={(COLUMN_HEIGHT - top) * zoom}
        enable={{
          bottom: !fromBucket && !disabled,
          top: false,
          right: false,
          left: false,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
        snapGap={snapGap}
        onResizeStart={(e) => {
          e.preventDefault();
        }}
        onResizeStop={onResizeStop}
      >
        {children}
      </Resizable>
    </Box>
  );
}

const useStyles = makeStyles<{ top: number }>()((_, { top }) => ({
  event: {
    overflowY: "clip",
    position: top ? "absolute" : "static",
    top,
    userSelect: "none",
    "&:focus": { outline: "none" },
  },
}));
